import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { BiExpandAlt, BiCollapse, BiChevronUp, BiChevronDown } from 'react-icons/bi';

const TimeSeriesChart = React.memo(({ 
  data = [], 
  width = '100%', 
  height = '100%', 
  margin = { top: 20, right: 30, bottom: 40, left: 60 },
  showGrid = true,
  showPoints = false,
  curveType = 'monotoneX',
  isExpanded = false,
  onToggleExpand,
  expansionLevel = 0,
  onVerticalExpand
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [resizeKey, setResizeKey] = useState(0);

  // Process data to ensure it's in the correct format
  const processedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data
      .filter(item => item && (item.date || item.time || item.timestamp) && (item.value !== undefined || item.count !== undefined))
      .map(item => ({
        date: new Date(item.date || item.time || item.timestamp),
        value: Number(item.value || item.count || 0)
      }))
      .sort((a, b) => a.date - b.date);
  }, [data]);



  // Handle resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Force re-render by updating the resize key
      setResizeKey(prev => prev + 1);
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || processedData.length === 0) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    // Get computed styles for CSS variables
    const getComputedStyle = (element, property) => {
      return window.getComputedStyle(element).getPropertyValue(property).trim();
    };
    
    const primaryColor = getComputedStyle(document.documentElement, '--primary-color') || '#ffcc00';
    const textColor = getComputedStyle(document.documentElement, '--text-color') || '#ccc';
    const gridColor = getComputedStyle(document.documentElement, '--grid-color') || '#ffffff';

    // Set up dimensions
    const containerRect = containerRef.current.getBoundingClientRect();
    const chartWidth = containerRect.width;
    const chartHeight = containerRect.height;
    const innerWidth = chartWidth - margin.left - margin.right;
    const innerHeight = chartHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr('width', chartWidth)
      .attr('height', chartHeight);

    // Create chart group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Set up scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(processedData, d => d.date))
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(processedData, d => d.value) * 1.1])
      .range([innerHeight, 0]);

    // Create line generator
    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX);



    // Add axes
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickFormat(d3.timeFormat('%b %d'))
      )
      .style('color', textColor);

    const yAxis = g.append('g')
      .call(d3.axisLeft(yScale)
        .tickFormat(d3.format('d'))
        .ticks(5)
      )
      .style('color', textColor);

    // Style axes
    xAxis.selectAll('path, line')
      .style('stroke', textColor)
      .style('stroke-opacity', 0.3);
    
    yAxis.selectAll('path, line')
      .style('stroke', textColor)
      .style('stroke-opacity', 0.3);

    // Add the line path
    const path = g.append('path')
      .datum(processedData)
      .attr('fill', 'none')
      .attr('stroke', primaryColor)
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('d', line);



    // Add data points if requested
    if (showPoints) {
      g.selectAll('.data-point')
        .data(processedData)
        .enter()
        .append('circle')
        .attr('class', 'data-point')
        .attr('cx', d => xScale(d.date))
        .attr('cy', d => yScale(d.value))
        .attr('r', 4)
        .attr('fill', primaryColor)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('opacity', 0)
        .transition()
        .delay((d, i) => i * 100)
        .duration(300)
        .style('opacity', 1);
    }

    // Add tooltip
    const tooltipGroup = svg.append('g')
      .attr('class', 'tooltip')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // Tooltip circle
    const tooltipCircle = tooltipGroup.append('circle')
      .attr('r', 5)
      .attr('fill', primaryColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Tooltip text
    const tooltipText = tooltipGroup.append('text')
      .attr('x', 10)
      .attr('y', -10)
      .attr('font-size', '12px')
      .attr('fill', textColor);

    // Add hover interaction
    const bisect = d3.bisector(d => d.date).left;

    svg.on('mousemove', (event) => {
      const [mouseX] = d3.pointer(event);
      const x0 = xScale.invert(mouseX - margin.left);
      const i = bisect(processedData, x0, 1);
      const d0 = processedData[i - 1];
      const d1 = processedData[i];
      const d = x0 - d0?.date > d1?.date - x0 ? d1 : d0;

      if (d) {
        const xPos = xScale(d.date) + margin.left;
        const yPos = yScale(d.value) + margin.top;

        // Calculate text position to avoid cutoff
        const textWidth = 60; // Approximate width of the value text
        const textX = xPos + textWidth > chartWidth - margin.right ? -15  : 15;

        tooltipGroup
          .style('opacity', 1)
          .attr('transform', `translate(${xPos},${yPos})`);

        tooltipCircle.attr('cx', 0).attr('cy', 0);
        
        tooltipText
          .attr('x', textX)
          .attr('y', -15)
          .text(d.value.toFixed(0));
      }
    });

    svg.on('mouseleave', () => {
      tooltipGroup.style('opacity', 0);
    });

  }, [processedData, width, height, margin, showGrid, showPoints, curveType, isExpanded, resizeKey]);

  if (processedData.length === 0) {
    return (
      <div style={{ 
        width: '100%', 
        height: 200, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--text-color, #666)',
        fontSize: '14px'
      }}>
        No data available
      </div>
    );
  }

  // Calculate height based on expansion state and level
  const getChartHeight = () => {
    if (!isExpanded) return 220;
    const baseExpandedHeight = 500;
    const expansionIncrement = 150; // Additional height per expansion level
    return baseExpandedHeight + (expansionLevel * expansionIncrement);
  };

  return (
    <div
      ref={containerRef}
      className="time-series-container"
      style={{ 
        width: '100%', 
        height: getChartHeight(),
        position: 'relative',
        transition: 'height 0.3s ease-in-out'
      }}
      onTransitionEnd={() => {
        // Force re-render when transition ends
        setResizeKey(prev => prev + 1);
      }}
    >
      <div style={{
        position: 'absolute',
        top: -30,
        right: 8,
        display: 'flex',
        gap: 4,
        zIndex: 10
      }}>
        {/* Vertical expansion controls - only show when expanded */}
        {isExpanded && onVerticalExpand && (
          <>
            <button
              onClick={() => onVerticalExpand('up')}
              disabled={expansionLevel <= 0}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '4px',
                color: expansionLevel <= 0 ? '#666' : '#999',
                padding: '6px',
                fontSize: '16px',
                cursor: expansionLevel <= 0 ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                minHeight: '28px',
                opacity: expansionLevel <= 0 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (expansionLevel > 0) e.target.style.color = '#ddd';
              }}
              onMouseLeave={(e) => {
                if (expansionLevel > 0) e.target.style.color = '#999';
              }}
              title="Reduce height"
            >
              <BiChevronUp />
            </button>
            <button
              onClick={() => onVerticalExpand('down')}
              disabled={expansionLevel >= 3}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '4px',
                color: expansionLevel >= 3 ? '#666' : '#999',
                padding: '6px',
                fontSize: '16px',
                cursor: expansionLevel >= 3 ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                minHeight: '28px',
                opacity: expansionLevel >= 3 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (expansionLevel < 3) e.target.style.color = '#ddd';
              }}
              onMouseLeave={(e) => {
                if (expansionLevel < 3) e.target.style.color = '#999';
              }}
              title="Increase height"
            >
              <BiChevronDown />
            </button>
          </>
        )}
        
        {/* Main expand/collapse button */}
        <button
          onClick={onToggleExpand}
          style={{
            background: 'none',
            border: 'none',
            borderRadius: '4px',
            color: '#999',
            padding: '8px',
            fontSize: '18px',
            cursor: 'pointer',
            transition: 'color 0.2s',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            minHeight: '32px',
          }}
          onMouseEnter={(e) => e.target.style.color = '#ddd'}
          onMouseLeave={(e) => e.target.style.color = '#999'}
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <BiCollapse />
          ) : (
            <BiExpandAlt />
          )}
        </button>
      </div>
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: '100%',
          background: 'transparent'
        }}
      />
    </div>
  );
});

TimeSeriesChart.displayName = 'TimeSeriesChart';

export default TimeSeriesChart;
